import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { MessageSquare, Mail, Send, Phone, Loader2, CheckCircle2, ExternalLink, Plug, ArrowDownLeft, ArrowUpRight } from 'lucide-react';
import { comms } from '../../lib/api';
import { toast } from 'sonner';
import MessagingView from '../MessagingView';

const when = (iso) => { try { return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); } catch { return ''; } };

function EmailTab() {
  const [rows, setRows] = useState(null);
  useEffect(() => { comms.log('email').then(setRows).catch(() => setRows([])); }, []);
  if (rows === null) return <div className="py-16 text-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;
  return (
    <Card className="border-slate-200 overflow-hidden" data-testid="email-log">
      {rows.length === 0 ? (
        <div className="p-10 text-center text-slate-500">
          <Mail className="w-10 h-10 mx-auto text-slate-300" />
          <p className="mt-3 font-semibold text-slate-700">No emails sent yet</p>
          <p className="text-sm mt-1">Every invoice or estimate you email a customer shows up here automatically.</p>
        </div>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs text-slate-500 uppercase tracking-wider">
            <tr><th className="px-4 py-3 text-left">To</th><th className="px-4 py-3 text-left">Subject</th><th className="px-4 py-3 text-left">Sent</th><th className="px-4 py-3 text-left">Status</th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map(r => (
              <tr key={r.id} className="hover:bg-slate-50" data-testid="email-row">
                <td className="px-4 py-3 font-medium text-slate-900">{r.to || '—'}</td>
                <td className="px-4 py-3 text-slate-600">{r.subject || '—'}</td>
                <td className="px-4 py-3 text-slate-500">{when(r.createdAt)}</td>
                <td className="px-4 py-3"><Badge className="bg-emerald-100 text-emerald-700 border-0 capitalize">{r.status}</Badge></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}

function TelegramTab() {
  const [cfg, setCfg] = useState(null);
  const [token, setToken] = useState('');
  const [chatId, setChatId] = useState('');
  const [saving, setSaving] = useState(false);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [log, setLog] = useState([]);

  const loadLog = useCallback(() => { comms.log('telegram').then(setLog).catch(() => {}); }, []);
  useEffect(() => { comms.telegramConfig().then((c) => { setCfg(c); setChatId(c.chatId || ''); }).catch(() => setCfg({ configured: false })); loadLog(); }, [loadLog]);

  const save = async () => {
    setSaving(true);
    try { const c = await comms.saveTelegram({ botToken: token || undefined, chatId }); setCfg(c); setToken(''); toast.success('Telegram settings saved'); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };
  const send = async () => {
    if (!text.trim()) return;
    setSending(true);
    try { await comms.sendTelegram(text, chatId || undefined); setText(''); toast.success('Sent to Telegram'); loadLog(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not send'); }
    finally { setSending(false); }
  };

  if (cfg === null) return <div className="py-16 text-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;

  return (
    <div className="grid lg:grid-cols-5 gap-5">
      <Card className="lg:col-span-2 p-5 border-slate-200 space-y-4 h-fit" data-testid="telegram-config">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-[#229ED9]/10 flex items-center justify-center"><Send className="w-4 h-4 text-[#229ED9]" /></div>
          <div className="font-bold text-slate-900">Telegram {cfg.configured && <Badge className="ml-1 bg-emerald-100 text-emerald-700 border-0">Connected</Badge>}</div>
        </div>
        <p className="text-xs text-slate-500 leading-relaxed">
          Free crew messaging & alerts. Create a bot with <b>@BotFather</b> on Telegram (<code>/newbot</code>), paste the token here,
          then add your crew chat/group ID. Members get instant job alerts.
        </p>
        <div>
          <Label className="text-xs">Bot Token {cfg.configured && <span className="text-emerald-600">(saved — leave blank to keep)</span>}</Label>
          <Input value={token} onChange={e => setToken(e.target.value)} placeholder="123456:ABC-DEF…" className="mt-1 h-10" data-testid="telegram-token-input" />
        </div>
        <div>
          <Label className="text-xs">Default Crew Chat ID</Label>
          <Input value={chatId} onChange={e => setChatId(e.target.value)} placeholder="-1001234567890" className="mt-1 h-10" data-testid="telegram-chatid-input" />
        </div>
        <Button onClick={save} disabled={saving} className="w-full bg-[#229ED9] hover:bg-[#1b87b8] gap-1.5" data-testid="telegram-save-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plug className="w-4 h-4" />}Save Connection
        </Button>
        {cfg.webhookSecretSet && (
          <p className="text-xs text-slate-500" data-testid="telegram-webhook-secret">
            Inbound messages: register <code>/api/telegram/webhook</code> with Telegram&apos;s setWebhook and pass this
            as <code>secret_token</code>: <code className="break-all">{cfg.webhookSecret}</code>. Updates without it are refused.
          </p>
        )}
        <a href="https://core.telegram.org/bots#how-do-i-create-a-bot" target="_blank" rel="noreferrer" className="text-xs text-[#229ED9] font-semibold inline-flex items-center gap-1">How to create a bot <ExternalLink className="w-3 h-3" /></a>
      </Card>

      <Card className="lg:col-span-3 p-5 border-slate-200 space-y-4">
        <div className="font-bold text-slate-900">Send a message to the crew</div>
        <textarea value={text} onChange={e => setText(e.target.value)} rows={3} disabled={!cfg.configured}
          placeholder={cfg.configured ? 'Type an alert for your crew…' : 'Connect Telegram first to send messages'}
          className="w-full text-sm border border-slate-200 rounded-lg p-3 disabled:bg-slate-50" data-testid="telegram-message-input" />
        <Button onClick={send} disabled={sending || !cfg.configured || !text.trim()} className="bg-[#8BC53F] hover:bg-[#6FA82C] gap-1.5" data-testid="telegram-send-btn">
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}Send
        </Button>
        <div className="pt-2 border-t border-slate-100">
          <div className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">Recent Telegram activity</div>
          <div className="space-y-2 max-h-[320px] overflow-y-auto" data-testid="telegram-log">
            {log.length === 0 ? <p className="text-sm text-slate-400">No Telegram messages yet.</p> : log.map(m => (
              <div key={m.id} className="flex items-start gap-2 text-sm p-2 rounded-lg hover:bg-slate-50">
                {m.direction === 'in' ? <ArrowDownLeft className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" /> : <ArrowUpRight className="w-4 h-4 text-[#8BC53F] mt-0.5 flex-shrink-0" />}
                <div className="min-w-0 flex-1">
                  <div className="text-slate-800 break-words">{m.text}</div>
                  <div className="text-[11px] text-slate-400">{m.direction === 'in' ? (m.from || 'Incoming') : 'You'} · {when(m.createdAt)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Card>
    </div>
  );
}

function SmsTab() {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({ accountSid: '', authToken: '', apiKeySid: '', apiKeySecret: '', fromNumber: '' });
  const [saving, setSaving] = useState(false);
  const [showAdv, setShowAdv] = useState(false);
  const [to, setTo] = useState('');
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [log, setLog] = useState([]);

  const loadLog = useCallback(() => { comms.log('sms').then(setLog).catch(() => {}); }, []);
  useEffect(() => {
    comms.smsConfig().then((c) => { setCfg(c); setForm(f => ({ ...f, accountSid: c.accountSid || '', apiKeySid: c.apiKeySid || '', fromNumber: c.fromNumber || '' })); }).catch(() => setCfg({ configured: false }));
    loadLog();
  }, [loadLog]);

  const save = async () => {
    setSaving(true);
    try { const c = await comms.saveSms(form); setCfg({ ...cfg, ...c }); setForm(f => ({ ...f, apiKeySecret: '' })); toast.success('Twilio settings saved'); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };
  const send = async () => {
    if (!to.trim() || !text.trim()) return;
    setSending(true);
    try { await comms.sendSms(to, text); setText(''); toast.success('Text sent'); loadLog(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not send'); }
    finally { setSending(false); }
  };
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  if (cfg === null) return <div className="py-16 text-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;

  return (
    <div className="grid lg:grid-cols-5 gap-5">
      <Card className="lg:col-span-2 p-5 border-slate-200 space-y-3 h-fit" data-testid="sms-config">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-purple-100 flex items-center justify-center"><Phone className="w-4 h-4 text-purple-600" /></div>
          <div className="font-bold text-slate-900">Twilio SMS {cfg.configured && <Badge className="ml-1 bg-emerald-100 text-emerald-700 border-0">Connected</Badge>}</div>
        </div>
        <p className="text-xs text-slate-500 leading-relaxed">Text customers from your Twilio number. The easiest setup: paste your <b>Account SID</b>, <b>Auth Token</b>, and <b>Twilio phone number</b> — all on your Twilio Console home page.</p>
        <div><Label className="text-xs">Account SID (AC…)</Label><Input value={form.accountSid} onChange={set('accountSid')} placeholder="ACxxxxxxxxxxxx" className="mt-1 h-9" data-testid="sms-account-sid" /></div>
        <div><Label className="text-xs">Auth Token {cfg.configured && <span className="text-emerald-600">(saved — blank keeps)</span>}</Label><Input type="password" value={form.authToken} onChange={set('authToken')} placeholder="••••••••••••" className="mt-1 h-9" data-testid="sms-auth-token" /></div>
        <div><Label className="text-xs">Your Twilio Number (E.164)</Label><Input value={form.fromNumber} onChange={set('fromNumber')} placeholder="+15625551234" className="mt-1 h-9" data-testid="sms-from-number" /></div>
        <button type="button" onClick={() => setShowAdv(s => !s)} className="text-xs text-slate-500 underline" data-testid="sms-advanced-toggle">{showAdv ? 'Hide' : 'Use an API Key instead (advanced)'}</button>
        {showAdv && (
          <div className="space-y-2 border-t border-slate-100 pt-2">
            <div><Label className="text-xs">API Key SID (SK…)</Label><Input value={form.apiKeySid} onChange={set('apiKeySid')} placeholder="SKxxxxxxxx" className="mt-1 h-9" data-testid="sms-apikey-sid" /></div>
            <div><Label className="text-xs">API Key Secret</Label><Input type="password" value={form.apiKeySecret} onChange={set('apiKeySecret')} placeholder="••••••••" className="mt-1 h-9" data-testid="sms-apikey-secret" /></div>
          </div>
        )}
        <Button onClick={save} disabled={saving} className="w-full bg-purple-600 hover:bg-purple-700 gap-1.5" data-testid="sms-save-btn">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plug className="w-4 h-4" />}Save Connection</Button>
        <a href="https://console.twilio.com/" target="_blank" rel="noreferrer" className="text-xs text-purple-600 font-semibold inline-flex items-center gap-1">Open Twilio Console <ExternalLink className="w-3 h-3" /></a>
      </Card>

      <Card className="lg:col-span-3 p-5 border-slate-200 space-y-4">
        <div className="font-bold text-slate-900">Send a text to a customer</div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><Label className="text-xs">To (phone)</Label><Input value={to} onChange={e => setTo(e.target.value)} placeholder="+15625551234" disabled={!cfg.configured} className="mt-1 h-9 disabled:bg-slate-50" data-testid="sms-to-input" /></div>
        </div>
        <textarea value={text} onChange={e => setText(e.target.value)} rows={3} disabled={!cfg.configured}
          placeholder={cfg.configured ? 'Type a text message…' : 'Connect Twilio first to send texts'}
          className="w-full text-sm border border-slate-200 rounded-lg p-3 disabled:bg-slate-50" data-testid="sms-message-input" />
        <Button onClick={send} disabled={sending || !cfg.configured || !to.trim() || !text.trim()} className="bg-[#8BC53F] hover:bg-[#6FA82C] gap-1.5" data-testid="sms-send-btn">{sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}Send Text</Button>
        <div className="pt-2 border-t border-slate-100">
          <div className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">Recent SMS activity</div>
          <div className="space-y-2 max-h-[300px] overflow-y-auto" data-testid="sms-log">
            {log.length === 0 ? <p className="text-sm text-slate-400">No texts yet.</p> : log.map(m => (
              <div key={m.id} className="flex items-start gap-2 text-sm p-2 rounded-lg hover:bg-slate-50">
                {m.direction === 'in' ? <ArrowDownLeft className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" /> : <ArrowUpRight className="w-4 h-4 text-[#8BC53F] mt-0.5 flex-shrink-0" />}
                <div className="min-w-0 flex-1"><div className="text-slate-800 break-words">{m.text}</div><div className="text-[11px] text-slate-400">{m.direction === 'in' ? (m.from || 'Incoming') : `To ${m.to}`} · {when(m.createdAt)}</div></div>
              </div>
            ))}
          </div>
        </div>
      </Card>
    </div>
  );
}

export default function CommunicationHub() {
  const [summary, setSummary] = useState(null);
  useEffect(() => { comms.summary().then(setSummary).catch(() => {}); }, []);

  // Each square selects its own tab - they were plain cards, so the office
  // tapped them and nothing happened.
  const [tab, setTab] = useState('messages');
  const chips = summary ? [
    { label: 'In-app messages', value: summary.messages, icon: MessageSquare, on: true, tab: 'messages' },
    { label: 'Emails sent', value: summary?.email?.count, icon: Mail, on: summary?.email?.configured, tab: 'email' },
    { label: 'Telegram', value: summary?.telegram?.count, icon: Send, on: summary?.telegram?.configured, tab: 'telegram' },
    { label: 'SMS / Calls', value: summary?.sms?.count, icon: Phone, on: summary?.sms?.configured, tab: 'sms' },
  ] : [];

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5">
      <div>
        <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">Communication Hub</h1>
        <p className="text-slate-500 mt-1 text-sm">Messages, email, and crew chat — all in one place.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {chips.map(c => (
          <Card key={c.label} role="button" tabIndex={0} onClick={() => setTab(c.tab)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setTab(c.tab); } }}
            className={`p-4 border-slate-200 flex items-center gap-3 min-w-0 cursor-pointer transition-colors hover:border-[#8BC53F] ${tab === c.tab ? 'border-[#8BC53F] ring-1 ring-[#8BC53F]/40' : ''}`}
            data-testid={`comm-chip-${c.tab}`}>
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${c.on ? 'bg-[#8BC53F]/10 text-[#6FA82C]' : 'bg-slate-100 text-slate-400'}`}><c.icon className="w-5 h-5" /></div>
            <div className="min-w-0">
              <div className="text-xl font-extrabold text-slate-900 leading-none">{c.value ?? 0}</div>
              <div className="text-xs text-slate-500 leading-tight flex items-center gap-1">{c.label}{c.on && <CheckCircle2 className="w-3 h-3 text-emerald-500" />}</div>
            </div>
          </Card>
        ))}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="messages" className="gap-1.5" data-testid="tab-messages"><MessageSquare className="w-4 h-4" />Messages</TabsTrigger>
          <TabsTrigger value="email" className="gap-1.5" data-testid="tab-email"><Mail className="w-4 h-4" />Email</TabsTrigger>
          <TabsTrigger value="telegram" className="gap-1.5" data-testid="tab-telegram"><Send className="w-4 h-4" />Telegram</TabsTrigger>
          <TabsTrigger value="sms" className="gap-1.5" data-testid="tab-sms"><Phone className="w-4 h-4" />SMS / Calls</TabsTrigger>
        </TabsList>
        <TabsContent value="messages" className="mt-4">
          <Card className="border-slate-200 overflow-hidden"><MessagingView heightClass="h-[calc(100vh-360px)] min-h-[420px]" /></Card>
        </TabsContent>
        <TabsContent value="email" className="mt-4"><EmailTab /></TabsContent>
        <TabsContent value="telegram" className="mt-4"><TelegramTab /></TabsContent>
        <TabsContent value="sms" className="mt-4"><SmsTab /></TabsContent>
      </Tabs>
    </div>
  );
}
